import { useState } from 'react';
import { ResultsItem, ResultSubItem, IconContent, AccordionToggle, PriorityIndicator, PriorityIndicatorGroup } from '../../../features/Report';
import { Tooltip, Callout } from '../../../components';

export const BrandingSection = ({ id, data }) => {
  const [urlExpanded, setUrlExpanded] = useState(false);
  const [custom404Expanded, setCustom404Expanded] = useState(false);

  const defaultCriteria = {
    status: 'info',
    iconType: 'info',
    importance: { level: 0, text: '' },
    solvability: { level: 0, text: '' },
    isEmpty: true,
    message: 'Data is unavailable.',
    data: null,
  };

  const url = data?.url || defaultCriteria;
  const custom404 = data?.custom404 || defaultCriteria;
  const errorCount = data?.errorCount || 0;
  const toImproveCount = data?.toImproveCount || 0;

  const displayUrl = url?.data?.displayUrl || '';
  const urlLength = url?.data?.length ?? 0;

  return (
    <ResultsItem 
      id={id}
      title="Branding" 
      tooltip="Your brand carries immense value. Following branding best practices, like featuring your brand name in your website URL, can help your business send positive signals to users and Google about 'who' you are, like 'Google' the search engine or 'Google' the Internet provider. That's why our SEO checker looks at a few key areas of your branding."
      errorCount={errorCount}
      toImproveCount={toImproveCount}
    >
      <ResultSubItem
        title="URL"
        tooltip="Your URL is your online address and should match your company or brand name."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={url?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {!url?.isEmpty && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <p className="text-primary">
                  <span className="text-strong">{displayUrl}</span>
                </p>
                <p className="text-secondary">
                  Length: {urlLength} character(s)
                </p>
              </div>
            )}
            
            {url?.isEmpty && (
              <p className="text-primary">{url?.message || 'No URL was found.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={urlExpanded}
            onClick={() => setUrlExpanded(!urlExpanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={urlExpanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's URL Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Include your core keyword in your URL</li>
                <li>Separate words with dashes</li>
                <li>Write URLs in lowercase, without apostrophes or other special characters</li>
              </ul>
            </IconContent>
          }
          sideContent={
            <PriorityIndicatorGroup>
              <PriorityIndicator text="Medium Impact" level={2} />
              <PriorityIndicator text="Easy to Solve" level={1} />
            </PriorityIndicatorGroup>
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/seo/glossary/what-is-seo-friendly-url/" target="_blank" rel="noopener noreferrer">
                    How to Create SEO-Friendly URLs
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/digital-marketing/learn/long-domain-name-vs-short/" target="_blank" rel="noopener noreferrer">
                    Long Domain Names vs Short URLs: Which is Better?
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>

      <ResultSubItem
        title="Custom 404 Page"
        tooltip="Your 404 page is where users land when arriving on a page that doesn't exist on your site. Custom 404 pages help you redirect users while keeping them on your site."
        content={
          <IconContent
            icon={
              <Tooltip 
                iconType={custom404?.iconType || 'info'} 
                showTooltip={false} 
              />
            }
          >
            {!custom404?.isEmpty && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <p className="text-primary">
                  Great, your website has a custom 404 error page.
                </p>
                <Callout>
                  <p>Custom 404 error pages are an opportunity for you to reduce customer frustration and link to other pages on your site.</p>
                </Callout>
              </div>
            )}
            
            {custom404?.isEmpty && (
              <p className="text-primary">{custom404?.message || 'No Custom 404 Page was found.'}</p>
            )}
          </IconContent>
        }
        sideContent={
          <AccordionToggle
            isExpanded={custom404Expanded}
            onClick={() => setCustom404Expanded(!custom404Expanded)}
          >
            Tips
          </AccordionToggle>
        }
        isExpanded={custom404Expanded}
      >
        <ResultSubItem
          backgroundColor="blue"
          title="WebFX's Custom 404 Page Tips:"
          content={
            <IconContent>
              <ul className="in-content-list">
                <li>Include your sitemap on your 404 page</li>
                <li>Have your custom 404 page apologize for the error</li>
                <li>Find and fix as many 404 errors as possible</li>
              </ul>
            </IconContent>
          }
          sideContent={
            <PriorityIndicatorGroup>
              <PriorityIndicator text="Medium Impact" level={2} />
              <PriorityIndicator text="Easy to Solve" level={1} />
            </PriorityIndicatorGroup>
          }
        />
        
        <ResultSubItem
          backgroundColor="blue"
          title="You might also like:"
          content={
            <IconContent>
              <ul className="in-content-list in-content-list--links">
                <li>
                  <a href="/blog/web-design/find-remove-broken-links/" target="_blank" rel="noopener noreferrer">
                    How to Find and Remove Broken Website Links
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
                <li>
                  <a href="/web-development/learn/what-does-404-mean/" target="_blank" rel="noopener noreferrer">
                    5 Reasons Your Site Says 404
                    <svg
                      className="arrow-icon"
                      width="12.502"
                      height="10.503"
                      viewBox="0 0 12.502 10.503"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M7.25098 0.5L11.751 5.25098L7.25098 10.003" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M0.750977 5.25098H11.751" stroke="#207DE9" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </a>
                </li>
              </ul>
            </IconContent>
          }
        />
      </ResultSubItem>
    </ResultsItem>
  );
};
